/**
 * ContextDial — a small ring next to the session/history control that shows how
 * full this session's context is. Click to compress older turns into a brief
 * (same action as the composer's Compress button). Turns amber once the context
 * is large enough to be worth compressing.
 */
import { Loader2 } from "lucide-react";
import { MIN_TOKENS_TO_COMPRESS } from "@/pages/multi-agent/ide/runMultiAgentContextCompress";

// Soft reference window purely for the fill visualization (not a hard limit).
const CONTEXT_WINDOW = 200_000;

export function ContextDial({
  tokens,
  onCompress,
  busy = false,
}: {
  tokens: number;
  onCompress: () => void;
  busy?: boolean;
}) {
  const pct = Math.max(0, Math.min(1, tokens / CONTEXT_WINDOW));
  const heavy = tokens >= MIN_TOKENS_TO_COMPRESS;
  const r = 8;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  const color = heavy ? "var(--ma-state-warn)" : "var(--ma-accent)";
  const kTokens = Math.round(tokens / 1000);

  const dialSvg = busy ? (
    <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color }} />
  ) : (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
      <circle cx="11" cy="11" r={r} fill="none" stroke="var(--ma-border)" strokeWidth="2.5" />
      <circle
        cx="11"
        cy="11"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circ}`}
        transform="rotate(-90 11 11)"
        style={{ transition: "stroke-dasharray 400ms var(--ma-ease, ease)" }}
      />
    </svg>
  );

  return (
    <div className="inline-flex items-center gap-1">
      {/* Dial — informational only; the fill shows context usage. */}
      <div
        className="relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
        title={`Session context ~${kTokens}k tokens`}
        aria-label={`Session context: ${kTokens}k tokens`}
      >
        {dialSvg}
      </div>
      {/* Compress link — only visible when context is heavy enough to matter. */}
      {heavy && !busy && (
        <button
          type="button"
          onClick={onCompress}
          className="ma-micro"
          style={{
            textTransform: "none",
            letterSpacing: 0,
            color: "var(--ma-state-warn)",
            cursor: "pointer",
            padding: "0 2px",
          }}
          title="Compress older turns to free up context"
        >
          Compress
        </button>
      )}
    </div>
  );
}
